import { type AutomergeUrl } from "@automerge/automerge-repo";
import { useDocHandle } from "@automerge/react";
import { focusedSketchBuilder } from "apprentices/dataParts/focusedSketch";
import { screenshotPartBuilder } from "apprentices/dataParts/screenshot";
import { useAutomergeStore } from "automerge-tldraw";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import type { DimShape } from "sketch/shapes/dim";
import type { GridlineProps } from "sketch/shapes/gridline";
import { emptySketchSnapshot } from "sketch/snapshot";
import {
  createShapeId,
  DefaultDashStyle,
  DefaultSizeStyle,
  Editor,
  react,
  Tldraw,
  type TLShapeId,
  type TLStoreSnapshot,
} from "tldraw";

import { useBearSandbox } from "@/components/BearSandboxProvider";
import {
  useWorkspace,
  useWorkspacePane,
  type WorkspaceContextPart,
} from "@/panes/Workspace";
import { CalloutTool, calloutToolOverride } from "@/sketch/callout-tool";
import { CalloutBindingUtil } from "@/sketch/CalloutBindingUtil";
import { CalloutShapeUtil } from "@/sketch/CalloutShapeUtil";
import { DimTool, dimToolOverride } from "@/sketch/dim-tool";
import { DimBindingUtil } from "@/sketch/DimBindingUtil";
import { DimShapeUtil } from "@/sketch/DimShapeUtil";
import { GridlineTool, gridlineToolOverride } from "@/sketch/gridline-tool";
import { GridlineShapeUtil } from "@/sketch/GridlineShapeUtil";
import { MeasuredSnapOverlayUtil } from "@/sketch/MeasuredSnapOverlayUtil";
import { registerPdfUnderlayHandler } from "@/sketch/pdf-underlay";
import {
  makeSketchAssetStore,
  SKETCH_ASSET_URLS,
} from "@/sketch/sketch-assets";
import { SketchCanvasLayer, SketchOverlays } from "@/sketch/SketchOverlays";
import { SketchStylePanel } from "@/sketch/SketchStylePanel";
import { SketchToolbar } from "@/sketch/SketchToolbar";

import "tldraw/tldraw.css";
import "@/sketch/sketch.css";

const SKETCH_SHAPE_UTILS = [GridlineShapeUtil, DimShapeUtil, CalloutShapeUtil];
const SKETCH_BINDING_UTILS = [DimBindingUtil, CalloutBindingUtil];
// Same static type as the default snap overlay, so it replaces it.
const SKETCH_OVERLAY_UTILS = [MeasuredSnapOverlayUtil];
const CUSTOM_TOOLS = [GridlineTool, DimTool, CalloutTool];
const CUSTOM_OVERRIDES = [
  gridlineToolOverride,
  dimToolOverride,
  calloutToolOverride,
];

/**
 * The drafting snap ladder in page units (inches). Snap tracks the working
 * scale: the finest increment on the ladder that still spans at least
 * SNAP_MIN_PX on screen, from 1' on a floorplan down to 1/32" at 4x full.
 */
const GRID_STEPS = [12, 6, 3, 1, 0.5, 0.25, 0.125, 0.0625, 0.03125];
const SNAP_MIN_PX = 8;

function gridStepForZoom(zoom: number): number {
  return GRID_STEPS.filter((s) => s * zoom >= SNAP_MIN_PX).at(-1) ?? 12;
}

// A stable identity: tldraw compares options shallowly and recreates the
// editor when it changes. The snap step always spans 8-24 screen px (see
// gridStepForZoom), so dots at 4x and 16x of it stay airy at every zoom;
// the stock gridSteps draw at 1x and pack the screen once snap tracks scale.
const SKETCH_TLDRAW_OPTIONS = {
  // A sketch is one canvas: no pages (the filesystem organizes drawings,
  // frames subdivide a canvas).
  maxPages: 1,
  edgeScrollSpeed: 0,
  gridSteps: [
    { min: -1, mid: 0, step: 4 },
    { min: -1, mid: 0, step: 16 },
  ],
};

function resetEditor(editor: Editor) {
  editor.blur({ blurContainer: false });
  editor.selectNone();
  editor.setCurrentTool("hand");
}

/** What to zoom to and select once the sketch is up: short ids from the
 *  projection (`s1`, `d2`) or gridline labels (`@A`). Empty means the whole
 *  sketch. A new object reveals again; the same object is inert. */
export interface SketchReveal {
  ids: string[];
}

interface SketchPaneProps {
  paneId: string;
  filename: string;
  url?: AutomergeUrl;
  reveal?: SketchReveal;
}

/** The tldraw ids a reveal names, for the shapes that exist. */
function revealShapeIds(editor: Editor, ids: string[]): TLShapeId[] {
  const shapes = editor.getCurrentPageShapes();
  return ids.flatMap((id) => {
    if (id.startsWith("@")) {
      const label = id.slice(1);
      return shapes
        .filter(
          (shape) =>
            shape.type === "gridline" &&
            (shape.props as GridlineProps).label === label,
        )
        .map((shape) => shape.id);
    }
    const shapeId = createShapeId(id);
    return editor.getShape(shapeId) ? [shapeId] : [];
  });
}

const SketchPane = ({ paneId, filename, url, reveal }: SketchPaneProps) => {
  const [editor, setEditor] = useState<Editor | null>(null);

  const { focusPane, isFocused } = useWorkspacePane(paneId);
  const { registerContextBuilder } = useWorkspace();

  useEffect(() => {
    if (!editor || !reveal) return;
    // Focus first: an unfocused pane resets its editor, selection included.
    focusPane();
    const ids = revealShapeIds(editor, reveal.ids);
    editor.select(...ids);
    if (ids.length === 0) {
      editor.zoomToFit({ animation: { duration: 300 } });
      return;
    }
    editor.zoomToSelection({ animation: { duration: 300 } });
  }, [editor, reveal, focusPane]);

  useEffect(
    () => () => {
      if (editor) resetEditor(editor);
    },
    [editor],
  );

  useEffect(() => {
    if (!editor) {
      return;
    }

    const unregister = registerContextBuilder(async () => {
      if (!editor) return undefined;
      const viewportBounds = editor.getViewportPageBounds();
      if (!viewportBounds) return undefined;

      const parts: WorkspaceContextPart[] = [];

      const focusedSketch = focusedSketchBuilder({
        editor,
        bounds: viewportBounds,
      });
      parts.push({
        type: "data-focusedSketch",
        id: paneId,
        data: focusedSketch,
      });

      const screenshot = await screenshotPartBuilder(editor, viewportBounds);
      parts.push({
        type: "data-screenshot",
        id: paneId,
        data: { screenshot: screenshot.screenshot },
      });

      return parts;
    });

    return unregister;
  }, [editor, paneId, registerContextBuilder]);

  useEffect(() => {
    if (!editor) {
      return;
    }

    if (isFocused) {
      editor.focus({ focusContainer: false });
      return;
    }

    resetEditor(editor);
  }, [editor, isFocused]);

  const handlePointerDownCapture = useCallback(() => {
    focusPane();
  }, [focusPane]);

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      event.stopPropagation();
    },
    [],
  );

  if (!url) {
    return (
      <div className="p-4 h-full flex items-center justify-center text-sm text-muted-foreground">
        {`Sketch file "${filename}" not found.`}
      </div>
    );
  }

  return (
    <div
      className="h-full w-full outline-none"
      data-workspace-pane
      onPointerDownCapture={handlePointerDownCapture}
      onPointerDown={handlePointerDown}
    >
      <AutomergeSketchEditor
        url={url}
        isFocused={isFocused}
        onEditorMount={setEditor}
      />
    </div>
  );
};

export default SketchPane;

interface AutomergeSketchEditorProps {
  url: AutomergeUrl;
  isFocused: boolean;
  onEditorMount(editor: Editor): void;
}

const AutomergeSketchEditor = ({
  url,
  isFocused,
  onEditorMount,
}: AutomergeSketchEditorProps) => {
  const handle = useDocHandle<TLStoreSnapshot>(url, { suspense: true });
  const { playground } = useBearSandbox();
  const assets = useMemo(() => makeSketchAssetStore(playground), [playground]);
  const store = useAutomergeStore({
    handle,
    shapeUtils: SKETCH_SHAPE_UTILS,
    bindingUtils: SKETCH_BINDING_UTILS,
    assets,
  });

  useEffect(() => {
    let didCancel = false;
    handle.whenReady().then(() => {
      if (didCancel) return;
      handle.change((doc) => {
        if (!doc.store) {
          Object.assign(doc, emptySketchSnapshot());
        }
      });
    });

    return () => {
      didCancel = true;
    };
  }, [handle]);

  return (
    <Tldraw
      licenseKey={import.meta.env.VITE_TLDRAW_LICENSE_KEY}
      store={store}
      assetUrls={SKETCH_ASSET_URLS}
      colorScheme="system"
      autoFocus={false}
      hideUi={!isFocused}
      options={SKETCH_TLDRAW_OPTIONS}
      shapeUtils={SKETCH_SHAPE_UTILS}
      bindingUtils={SKETCH_BINDING_UTILS}
      overlayUtils={SKETCH_OVERLAY_UTILS}
      tools={CUSTOM_TOOLS}
      overrides={CUSTOM_OVERRIDES}
      components={{
        HelpMenu: null,
        NavigationPanel: null,
        MainMenu: null,
        Toolbar: SketchToolbar,
        StylePanel: SketchStylePanel,
        InFrontOfTheCanvas: SketchOverlays,
        OnTheCanvas: SketchCanvasLayer,
      }}
      onMount={(instance) => {
        onEditorMount(instance);
        instance.setCurrentTool("hand");
        // Detail work needs detail scales: the stock ceiling of 8 is about
        // 1" = 1'-0". 96 is full size on a 96 dpi screen; 384 (4x full)
        // makes even a 1/32" feature legible.
        instance.setCameraOptions({
          zoomSteps: [0.05, 0.1, 0.25, 0.5, 1, 2, 4, 8, 16, 32, 96, 192, 384],
        });
        // A refresh mid-gesture can strand the dim tool's provisional as a
        // degenerate zero-length dim; sweep them before framing. An empty
        // callout is NOT swept: the sketch is shared, so a peer typing a
        // note has an empty one in the document for as long as they take to
        // type it, and this would delete it out from under them. Editing
        // ending is what decides a callout is a stray (CalloutShapeUtil's
        // onEditEnd), which covers every way out except killing the tab.
        const stranded = instance.getCurrentPageShapes().filter((s) => {
          if (s.type !== "dim") return false;
          const { start, end } = (s as DimShape).props;
          return (
            start.x === end.x &&
            start.y === end.y &&
            instance.getBindingsFromShape(s.id, "dim").length === 0
          );
        });
        if (stranded.length) {
          instance.deleteShapes(stranded.map((s) => s.id));
        }
        // Frame the sheet like a drafter: content zooms to fit; an empty
        // sketch opens with the origin near the bottom left, x running
        // right and y running up.
        if (instance.getCurrentPageShapes().length) {
          instance.zoomToFit({ animation: { duration: 0 } });
        } else {
          const viewport = instance.getViewportScreenBounds();
          instance.setCamera({ x: 140, y: viewport.h - 140, z: 1 });
        }
        // Graph paper whose snap tracks the working scale: every tool
        // (native and drafting) snaps through gridSize, so one reactive
        // rule serves them all. Settles in one extra pass: the write makes
        // the equality check true on the re-run.
        instance.updateInstanceState({ isGridMode: true });
        const stopGridStep = react("sketch grid step", () => {
          const step = gridStepForZoom(instance.getZoomLevel());
          if (instance.getDocumentSettings().gridSize !== step) {
            instance.updateDocumentSettings({ gridSize: step });
          }
        });
        // Drafted, not whiteboarded: new geometry defaults to thin solid
        // strokes (the freehand pencil stays sketchy, which is its job).
        instance.setStyleForNextShapes(DefaultDashStyle, "solid");
        instance.setStyleForNextShapes(DefaultSizeStyle, "s");
        // Alignment guides on every drag, no modifier key required.
        instance.user.updateUserPreferences({ isSnapMode: true });
        registerPdfUnderlayHandler(instance, playground);
        return stopGridStep;
      }}
    />
  );
};
